import { CardFilterParams, CardsList } from '../interfaces/cards';
import { CommonResponse } from '../interfaces/categories';
import axiosInstance from '../axios';
import { AxiosError } from 'axios';
import { WishboardTheme } from '@/app/components/board-view/ThemeSheet';

/**
 * Service for fetching card data from the API
 */
export const CardService = {
  /**
   * Get cards by category slug
   * @param slug - Category slug
   * @param params - Filter parameters
   * @returns Promise with the cards data
   */
  getCardsBySlug: async (slug: string, params: CardFilterParams): Promise<CommonResponse> => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (params.filters) {
        // Handle multiple filters
        const filterArray = params.filters.split(',');
        filterArray.forEach(filter => {
          queryParams.append('filters', filter);
        });
      }

      if (params.is_latest) {
        queryParams.append('is_latest', params.is_latest.toString());
      }

      if (params.is_staff_pick) {
        queryParams.append('is_staff_pick', params.is_staff_pick.toString());
      }

      if (params.sort_by) {
        queryParams.append('sort_by', params.sort_by);
      }

      if (params.sort_order) {
        queryParams.append('sort_order', params.sort_order);
      }

      if (params.page) {
        queryParams.append('page', params.page.toString());
      }

      // Make the API request
      const response = await axiosInstance.get(`/cards/list/slug/${slug}/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching cards:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: []
      };
    }
  },

  /**
   * Get card detail by ID
   * @param id - Card ID
   * @returns Promise with the card detail
   */
  getCardDetail: async (id: string): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.get(`/cards/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching card detail:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: {}
      };
    }
  },

  /**
   * Get card detail by slug
   * @param slug - Card slug
   * @returns Promise with the card detail
   */
  getCardDetailBySlug: async (slug: string): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.get(`/group-cards/${slug}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching card detail by slug:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: {}
      };
    }
  },

  /**
   * Slug + last-modified date of every live card design (for the sitemap).
   * Never throws: an empty list just leaves card pages out of the sitemap.
   */
  getSitemapEntries: async (): Promise<{ slug: string; updated_at: string }[]> => {
    try {
      const response = await axiosInstance.get('/cards/sitemap/');
      return response.data?.response_status ? response.data.response_data : [];
    } catch (error) {
      console.error('Error fetching card sitemap entries:', error);
      return [];
    }
  },

  /**
   * Get filters for a category
   * @param categoryId - Category ID
   * @returns Promise with the filters data
   */
  getFilters: async (categoryId: string): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.get(`/filters/category/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching filters:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: []
      };
    }
  },

  /**
   * Update board theme
   * @param slug - Card slug
   * @param board_theme - Theme name
   * @returns Promise with the updated board data
   */
  updateBoardTheme: async (slug: string, board_theme: WishboardTheme): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.put(`/group-cards/${slug}/board-theme/`, { board_theme });
      return {
        response_status: true,
        response_message: 'Theme updated successfully',
        response_data: response.data
      };
    } catch (error) {
      console.error('Error updating board theme:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: {}
      };
    }
  }

};
